import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from 'vopay';
import { UtilButton } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import useElinx from '@/components/Elinx/ElinxHistory/Details/useElinx';

type UseElinxDetailsParams = {
  ELinxRequestID: string;
  jwt: string;
};

export default function useElinxDetails({ ELinxRequestID, jwt }: UseElinxDetailsParams) {
  const history = useHistory();
  const toast = useToast();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ElinxTransactionInfo',
  });
  const { details, documentLink, documentName, fetchElinxDetails, fetchDocument } = useElinx({ bearer: jwt });

  useEffect(() => {
    if (!ELinxRequestID) return;
    fetchElinxDetails({ ELinxRequestID }).catch(() => {
      toast.neutral(t('ErrorMessages.fetchingError', { defaultValue: 'Failed to fetch eLinx details' }));
    });
  }, [ELinxRequestID]);

  useEffect(() => {
    if (!details?.DocumentID) return;
    fetchDocument({ DocumentID: details.DocumentID }).catch(() => {
      toast.neutral(t('ErrorMessages.fetchingDocumentError', { defaultValue: 'Failed to fetch the invoice' }));
    });
  }, [details?.DocumentID]);

  const elinxAdditionalId = ELinxRequestID ? (
    <Box flx="col" gapSize="6px">
      <div>{t('TransactionDetails.elinxId', { defaultValue: 'eLinx ID' })}</div>
      <UtilButton onClick={() => history.push(`/elinx-history/details/${ELinxRequestID}`)}>{ELinxRequestID}</UtilButton>
    </Box>
  ) : undefined;

  const invoiceButton = documentLink ? (
    <UtilButton
      size="medium"
      iconRightProps={{ icon: 'visibility', fillAmount: 1 }}
      onClick={() => window.open(documentLink, '_blank')}
    >
      {documentName || t('TransactionDetails.invoice', { defaultValue: 'Invoice' })}
    </UtilButton>
  ) : undefined;

  return { details, documentLink, documentName, elinxAdditionalId, invoiceButton };
}
